import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiScopes, loginRequest, msalInstance } from './msalConfig';

/**
 * Récupère un access token API silencieusement (refresh auto si possible).
 * En cas d'échec → relance un flow REDIRECT.
 *
 * Cette fonction est appelée par le client API (intercepteur fetch).
 * Elle est exportée hors hook pour pouvoir être appelée n'importe où.
 */
export async function acquireApiToken(): Promise<string> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) {
    // Pas de session → on déclenche le redirect login
    await msalInstance.loginRedirect(loginRequest);
    // loginRedirect navigue, le code en dessous ne sera jamais exécuté
    throw new Error('Redirecting to login...');
  }

  try {
    const result = await msalInstance.acquireTokenSilent({
      account,
      scopes: apiScopes
    });
    return result.accessToken;
  } catch (err) {
    // Token expiré non renouvelable → consentement requis ou MFA
    if (err instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect({ scopes: apiScopes });
      throw new Error('Redirecting to consent...');
    }
    throw err;
  }
}

/**
 * Hook simple pour récupérer l'utilisateur courant (claims du token ID).
 */
export function useAuth() {
  const { accounts, instance } = useMsal();
  const account = accounts[0];

  return {
    isAuthenticated: !!account,
    name: account?.name ?? '',
    email: account?.username ?? '',
    login: () => instance.loginRedirect(loginRequest),
    logout: () => instance.logoutRedirect()
  };
}
